<template>
  <div class="grid gap-4 grid-cols-1 md:grid-cols-3 lg:grid-cols-4 pt-40 pb-32">
    <!-- Filters (Simplified for now, can copy full logic if needed) -->
    <div
      class="col-span-1 w-full h-fit border-2 border-(--main-color-500) rounded-lg p-4 ms-2"
    >
      <div
        class="grid grid-cols-7 justify-items-center md:justify-items-start items-center"
      >
        <div
          class="text-(--main-color-500) font-bold col-span-7 md:col-span-full mb-4 md:mb-0"
        >
          Filtre
        </div>
        <UInput
          v-model="searchTerm"
          color="neutral"
          highlight
          placeholder="Search..."
          size="lg"
          class="w-full mt-0 md:mt-4 col-span-3 md:col-span-full pr-2 md:pr-0"
        />
        <div
          class="grid gap-2 md:gap-4 grid-cols-2 col-span-4 md:col-span-full w-full"
        >
          <UInput
            v-model="minPrice"
            color="neutral"
            highlight
            placeholder="Prix min"
            size="lg"
            class="w-full mt-0 md:mt-4"
            type="number"
          />
          <UInput
            v-model="maxPrice"
            color="neutral"
            highlight
            placeholder="Prix max"
            size="lg"
            class="w-full mt-0 md:mt-4"
            type="number"
          />
        </div>

        <div
          class="grid gap-2 md:gap-4 grid-cols-2 col-span-4 md:col-span-full w-full mt-4"
        >
          <UCheckbox v-model="isHalal" label="Halal" color="main" size="lg" />
          <UCheckbox v-model="isVegan" label="Vegan" color="main" size="lg" />
        </div>
      </div>
    </div>

    <!-- Menu Grid -->
    <div
      class="col-span-2 lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-hidden justify-items-center"
    >
      <MenuFlipCard v-for="menu in filteredMenus" :key="menu.id" :menu="menu" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Menu, MenuContent } from "~/models/menu";

const { $api } = useNuxtApp();
const { errorToast } = useToastMessages();
// Pre-capture apiUrl before any await (SSR async context safety)
const apiUrl = useRuntimeConfig().public.apiUrl;

const searchTerm = ref("");
const minPrice = ref("");
const maxPrice = ref("");
const isHalal = ref(false);
const isVegan = ref(false);

const { data: menus, refresh: refreshMenus } = await useAsyncData("menus", async () => {
  try {
    const response = await $api.get("/menus");
    if (response && Array.isArray(response)) {
      return response.map((menu: Menu) => ({
        ...menu,
        image: getImageUrl(menu.image, apiUrl),
        menu_content: (menu.menu_content || []).map((mc: MenuContent) => ({
          ...mc,
          repas: mc.repas
            ? {
                ...mc.repas,
                image: getImageUrl(mc.repas.image, apiUrl),
                content: mc.repas.content || [],
              }
            : mc.repas,
        })),
      }));
    }
    return [];
  } catch (e) {
    console.error("[menus] SSR/fetch error:", e);
    if (import.meta.client) {
      errorToast(
        "Erreur menus",
        "Une erreur est survenue lors du chargement des menus"
      );
    }
    return [];
  }
});

onMounted(async () => {
  if (!menus.value || menus.value.length === 0) {
    await refreshMenus();
  }
});

const filteredMenus = computed(() => {
  if (!menus.value) return [];
  return menus.value.filter((menu: Menu) => {
    // Search filter
    if (
      searchTerm.value &&
      !menu.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    ) {
      return false;
    }

    // Price filter
    if (minPrice.value && menu.price < parseFloat(minPrice.value)) {
      return false;
    }
    if (maxPrice.value && menu.price > parseFloat(maxPrice.value)) {
      return false;
    }

    // Halal filter
    if (isHalal.value && !menu.is_halal) {
      return false;
    }

    // Vegan filter
    if (isVegan.value && !menu.is_vegan) {
      return false;
    }

    return true;
  });
});
</script>
